// Rich-UI artifact spec types (POC, `nadia_artifacts`). Mirror of
// coach_core/coach_core/artifacts/richtree.py — keep field names in sync; the
// backend validates strictly, this side trusts the stored JSON.

export type RichTone = "info" | "success" | "warning" | "danger";

export interface RichHeading {
    type: "heading";
    text: string;
    level?: 2 | 3;
}

export interface RichText {
    type: "text";
    markdown: string;
}

export interface RichCallout {
    type: "callout";
    body: string;
    title?: string | null;
    tone?: RichTone;
}

export interface RichStats {
    type: "stats";
    items: { label: string; value: string; delta?: string | null }[];
}

export interface RichBarChart {
    type: "bar_chart";
    labels: string[];
    values: number[];
    title?: string | null;
    unit?: string | null;
}

export interface RichChecklist {
    type: "checklist";
    items: { label: string; done?: boolean; note?: string | null }[];
}

export interface RichProgress {
    type: "progress";
    label: string;
    value: number;
}

export interface RichTable {
    type: "table";
    columns: string[];
    rows: string[][];
    caption?: string | null;
}

export interface RichSteps {
    type: "steps";
    items: { title: string; detail?: string | null }[];
}

export interface RichDivider {
    type: "divider";
}

export type RichBlock = RichHeading | RichText | RichCallout | RichStats | RichBarChart | RichChecklist | RichProgress | RichTable | RichSteps | RichDivider;

export interface RichUiSpec {
    version: 1;
    blocks: RichBlock[];
}

// Anything else — older artifacts, a shape from a newer spec, or a malformed tree —
// renders as Markdown. Every block must be an object carrying a string `type` so the
// block walker never throws mid-render.
export const parseRichSpec = (content: string): RichUiSpec | null => {
    try {
        const parsed: unknown = JSON.parse(content);
        if (typeof parsed !== "object" || parsed === null) {
            return null;
        }
        const blocks = (parsed as RichUiSpec).blocks;
        if (!Array.isArray(blocks)) {
            return null;
        }
        for (const block of blocks) {
            if (typeof block !== "object" || block === null || typeof block.type !== "string") {
                return null;
            }
        }
        return parsed as RichUiSpec;
    } catch {
        // not JSON — markdown fallback
    }
    return null;
};
